import AppContext from "../../../framework/AppContext";
import AppBar from "../../../framework/component/frame/AppBar";
import React, {ReactNode} from "react";
import {useParams} from "react-router-dom";
import MessagePanel from "../../../framework/component/container/MessagePanel";
import {MessagePanelType} from "../../../framework/component/container/MessagePanelType";

/**
 * The URL the cancel btn will redirect to
 */
const REDIRECT_URL = "/ams";

/**
 * Store the messages in this map so they're more reusable
 */
type MessageType = "success" | "expired-link" | "already-signed-up";
const MESSAGES: Record<MessageType, { title: string, body: ReactNode, type: MessagePanelType }> = {
  success: {
    title: "Thank you for signing up",
    body: <p>
      An email has been sent to the address you supplied. Please click on the link in this email to complete the sign up
      process. If you don't receive an email, please check your spam email folder before trying again or contacting UK
      Biobank.
    </p>,
    type: MessagePanelType.INFO
  },
  "expired-link": {
    title: "Your invitation link has expired",
    body: <p>
      Please contact the Principal Investigator of the project you have been added to so they can send you a new link.
      Please note this link is valid for 24 hours once sent.
    </p>,
    type: MessagePanelType.ERROR
  },
  "already-signed-up": {
    title: "You have already signed up!",
    body: <p>
      You have previously signed up to use UK Biobank resources. Please log in and complete a registration.
    </p>,
    type: MessagePanelType.WARNING
  }
};

export default function SignUpMessage() {
  let context!: React.ContextType<typeof AppContext>
  const {messageType} = useParams<{ messageType: string }>();
  const requestedMessage = (messageType ?? "").toLowerCase();

  const isValid = (val: string): val is MessageType => {
    return (["success", "expired-link", "already-signed-up"]).includes(val as MessageType);
  };

  if (!isValid(requestedMessage)) {
    return <div id={"signUpMessagePage"} className="page">
      <AppContext.Provider value={context}>
        <AppBar/>
        <MessagePanel title={"Unexpected error"} panelType={MessagePanelType.ERROR}>
          There was an unexpected error when loading the page. Please try again later
        </MessagePanel>
      </AppContext.Provider>
    </div>;
  }

  const {title, body, type} = MESSAGES[requestedMessage];
  return <div id={"signUpMessagePage"} className="page">
    <AppContext.Provider value={context}>
      <AppBar/>
      <div className={"content-area non-menu-page"}>
        <MessagePanel title={title} panelType={type} data-testid="message">
          {body}
        </MessagePanel>
        {requestedMessage !== "expired-link" &&
            <div id={"signUpMessageBtnArea"} className={"btnArea"}>
              <a id="signUpMessageGoToLogIn" href={REDIRECT_URL} className="btn btn-primary">Go back to login page</a>
            </div>
        }
      </div>
    </AppContext.Provider>
  </div>;
}